import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { selectCount, selectTotal } from "../features/counter/counterSlice";
import CartItem from "../Components/CartItem";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { usePaymentMode } from "../context/PaymentModeContext";


const STRIPE_PK_BY_AFFILIATION = {
  live: {
    UST: "pk_live_51HC8ruLYpiPz9FF4gF6Suk6enWcV7kocqHJdU1jMWm1sck1SlJCrsUftYPDDwWoZ8L6Txo9olw0l6Yv5NAZs2eDJ00KJiF3o00",
    AOSH: "pk_live_51SdXZxH78Av705i4BAPgTkkg1UXpnX0iDCJVjXsv1qzbbDGb7XBZJdPOAFR4Byavg5gCIksVTYHR7VmiiRntYJ0g00UXNHWE14",
  },
  test: {
    UST: "pk_test_51HC8ruLYpiPz9FF416QMwOiY1LJDy6oF3JqZ5YuATtIreOoh6ppEnQk5FQlhFzcpWumBZTefBvznFz595REDm48g00uktcs3u7",
    AOSH: "pk_test_51SdXZxH78Av705i4nWu0dMWDA4sq805vkvrP4pfuI0V3frJp3zCh88WTng5H0gcRTyO3OmE91tHx4ZBrn86Yemo500NXbW2Axs",
  }
};


function getCartAffiliation(items = []) {
  const affs = new Set(
    items
      .map((i) => i.affiliation || i.courseAffiliation)
      .filter(Boolean)
  );

  if (affs.size === 0) return null;
  if (affs.size > 1) return "MIXED";
  return [...affs][0]; // "UST" or "AOSH"
}


function Basket() {
  const ref = useRef(null);

  const items = useSelector(selectCount);
  // console.log(items);
  const total = useSelector(selectTotal);

  const [user] = useState(JSON.parse(localStorage.getItem("user")));

  const [teamUsers, setTeamUsers] = useState(
    JSON.parse(localStorage.getItem("teamUsers"))
  );

  const [, setUserTeam] = useState(null);
  const [totalPrice, setTotalPrice] = useState(total);

  const [selectedCheckoutUsers, setSelectedCheckoutUsers] = useState([]);

  const [teamCheckOut] = useState([]);

  const [, setClickedOutside] = useState(true);
  const [itemsQuantity, setItemsQuantity] = useState(1);
  const { mode } = usePaymentMode();

  useEffect(() => {
    console.log(mode)
  }, [mode])

  useEffect(() => {
    setTotalPrice(total);
  }, [total]);

  const mergedTeamUsersRef = useRef(false);

  useEffect(() => {
    if (mergedTeamUsersRef.current) return; // ✅ prevents loops
    if (!Array.isArray(teamUsers) || teamUsers.length === 0) return;

    mergedTeamUsersRef.current = true;

    localStorage.removeItem("teamUsers");
    setTeamUsers(null);

    setSelectedCheckoutUsers((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const incoming = teamUsers;

      const seen = new Set(prevArr.map(u => u?.userEmail).filter(Boolean));
      const toAdd = incoming.filter(u => u?.userEmail && !seen.has(u.userEmail));

      return [...prevArr, ...toAdd];
    });

    setItemsQuantity((prev) => prev + teamUsers.length);
  }, [teamUsers]);

  useEffect(() => {
    if (!user?.email) return;

    let isMounted = true;

    axios.post("/returnTeams", { email: user.email }).then((data) => {
      if (!isMounted) return;
      setUserTeam(data.data);
    });

    // ✅ add self once (dedupe)
    setSelectedCheckoutUsers((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const alreadyHasSelf = prevArr.some(
        (u) => (u?.userEmail || "").toLowerCase() === user.email.toLowerCase()
      );
      if (alreadyHasSelf) return prevArr;

      return [
        ...prevArr,
        { userEmail: user.email, type: "Self" },
      ];
    });

    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    // console.log("Team new members:", teamCheckOut);
  }, [teamCheckOut]);

  const onClickCheckout = async () => {
    if (!user || selectedCheckoutUsers.length === 0) return;

    const affiliation = getCartAffiliation(items);

    if (affiliation === "MIXED") {
      alert("Your cart contains both UST and AOSH courses. Please check out separately.");
      return;
    }
    if (!affiliation) {
      alert('Could not determine cart affiliation. Each item must have affiliation: "UST" or "AOSH".');
      return;
    }

    const publishableKey = STRIPE_PK_BY_AFFILIATION[mode]?.[affiliation];
    if (!publishableKey) {
      alert(`Unknown affiliation: ${mode}/${affiliation}`);
      return;
    }

    let stripeSession;
    try {
      stripeSession = await axios.post("/create-checkout-session", {
        customerID: user.UserID,
        items,
        email: user.email,
        users: selectedCheckoutUsers,
        quantity: itemsQuantity,
        teamId: "self",
        affiliation,
        requestedMode: mode,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to create checkout session.";
      alert(msg);
      return;
    }

    const stripe = await loadStripe(publishableKey);
    if (!stripe) {
      alert("Stripe failed to load. Please refresh and try again.");
      return;
    }

    const result = await stripe.redirectToCheckout({
      sessionId: stripeSession.data.id,
    });

    if (result.error) alert(result.error.message);
  };

  

  const handleClickOutside = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setClickedOutside(true);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  });

  useEffect(() => {
    // console.log("Changing:", selectedCheckoutUsers);
  }, [selectedCheckoutUsers]);

  useEffect(() => {
    let newTotal = total;
    newTotal = Number.parseFloat(newTotal) * Number.parseFloat(itemsQuantity);
    newTotal = newTotal.toFixed(2);
    setTotalPrice(newTotal);
  }, [itemsQuantity, total]);

  return (
    <Container>
      {/* Left */}
      <BasketItemsContainer>
        {items.length === 0
          ? "There are no items in the cart."
          : items.map((item) => (
              <CartItem
                key={item.id}
                id={item.id}
                title={item.name}
                description={item.description}
                price={item.price}
                quantity={itemsQuantity}
              />
            ))}
      </BasketItemsContainer>

      {/* Right */}
      <PaymentDetailsContainer>
        {items.length > 0 && (
          <RightSideMenu>
            <PaymentDetails>
              <SubTotal>
                SubTotal ({items.length} item{items.length !== 1 && "s"}):
              </SubTotal>
              <PriceTotal>${totalPrice}</PriceTotal>
              <IndividualCheckOut onClick={() => onClickCheckout()} role="link">
                Check Out
              </IndividualCheckOut>
            </PaymentDetails>
          </RightSideMenu>
        )}
      </PaymentDetailsContainer>
    </Container>
  );
}

export default Basket;

const Container = styled.div`
  /* position: relative; */
  /* width: 100%; */
  /* padding: 40px; */
  /* height: 100%; */
  padding: 70px 0px 20px 0px;
  display: grid;
  grid-template-columns: auto 300px;
  grid-gap: 10px;
  /* background: brown; */
`;

const BasketItemsContainer = styled.div`
  /* width: 100%; */
  /* height: 100%; */
  /* background: yellow; */
  display: grid;
  // grid-template-columns: auto;
  // grid-template-rows: repeat(auto-fill, minmax(100%, 1fr));
  grid-auto-rows: auto;
  grid-gap: 20px;
  padding: 2rem;
  z-index: 0;
`;
const PaymentDetailsContainer = styled.div`
  /* position: relative; */
  /* padding: 2rem; */
  padding-top: 2rem;
  padding-right: 2rem;
`;

const PaymentDetails = styled.div`
  /* position: absolute;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
  height: 210px; */
  /* margin: 10px; */
  /* margin-top: 2rem;
  width: 16rem;
  padding: 20px; */

  box-shadow: 0px 2px 10px 0px rgb(0 0 0 / 10%);
  /* height: 145px; */
  padding: 20px;
`;

const SubTotal = styled.div`
  font-size: 1rem;
`;
const PriceTotal = styled.div`
  padding: 10px;
  font-weight: 700;
  font-size: 2rem;
`;
const IndividualCheckOut = styled.button`
  padding: 15px;
  width: 100%;
  outline: none;
  border: none;
  border-radius: 4px;
  background-color: #f26669;
  color: white;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background-color: #f15b5d;
  }
`;

const RightSideMenu = styled.div``;